export class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.anchor = this.querySelector("a");
      if (!this.anchor) return;

      this.isLoading = false;
      // Trigger loading when element is this many pixels away from entering viewport
      // Increase this value to load even earlier
      this.threshold = 5000;

      this.checkPosition = this.checkPosition.bind(this);

      // Check on scroll/touch events - more efficient than RAF loop
      window.addEventListener("scroll", this.checkPosition, { passive: true });
      window.addEventListener("touchmove", this.checkPosition, { passive: true });
      window.addEventListener("resize", this.checkPosition, { passive: true });
      
      // Initial check
      this.checkPosition();
    }

    checkPosition() {
      if (this.isLoading || !this.anchor) return;

      // Get element's position relative to viewport
      const rect = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Load when element is within (viewportHeight + threshold) pixels from top of viewport
      if (rect.top < viewportHeight + this.threshold) {
        this.loadNextPage();
      }
    }

    async loadNextPage() {
      this.isLoading = true;
      window.removeEventListener("scroll", this.checkPosition);
      window.removeEventListener("touchmove", this.checkPosition);

      this.anchor.style.display = "flex";
      this.anchor.innerText = "Loading...";

      const href = this.anchor.getAttribute("href");
      if (!href) return;

      const nextPageUrl = new URL(href, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);
      const nextParams = new URLSearchParams(nextPageUrl.search);
      const pageValue = nextParams.get("page");
      const sectionIdValue = nextParams.get("section_id");

      const isAbTestActive = this.dataset.abTestActive === "true";

      if (isAbTestActive) {
        // trust next-page href, and only recover filter/sort if href lost them.
        const nextHasFilterParams = Array.from(nextParams.keys()).some((key) => key.startsWith("filter."));
        const currentHasFilterParams = Array.from(currentParams.keys()).some((key) => key.startsWith("filter."));

        if (!nextHasFilterParams && currentHasFilterParams) {
          Array.from(currentParams.entries())
            .filter(([key]) => key.startsWith("filter."))
            .forEach(([key, value]) => {
              nextParams.delete(key);
              nextParams.append(key, value);
            });
        }

        if (!nextParams.has("sort_by") && currentParams.has("sort_by")) {
          nextParams.set("sort_by", currentParams.get("sort_by"));
        }
      }

      if (pageValue) nextParams.set("page", pageValue);
      if (sectionIdValue) nextParams.set("section_id", sectionIdValue);

      nextPageUrl.search = nextParams.toString();

      try {
        const response = await fetch(nextPageUrl.toString());
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, "text/html");

        // Grab the product grid from the matching collection-info scope
        const newGrid = html.querySelector("collection-info [data-product-grid]");
        const collectionInfo = this.closest("collection-info");
        const grid = collectionInfo ? collectionInfo.querySelector("[data-product-grid]") : document.querySelector("[data-product-grid]");

        const isEmptyStateGrid = newGrid && newGrid.children.length === 1
          && newGrid.firstElementChild?.tagName === "P";

        if (newGrid && grid && !isEmptyStateGrid) {
          // Use insertAdjacentHTML for faster DOM insertion (single reflow)
          grid.insertAdjacentHTML("beforeend", newGrid.innerHTML);
        }

        // Handle next infinite scroll
        const newInfinite = html.querySelector("collection-info infinite-scroll");
        const hasValidAnchor = newInfinite && newInfinite.querySelector("a[href]");
        
        if (hasValidAnchor && !isEmptyStateGrid) {
          this.replaceWith(newInfinite);
        } else {
          // Last page reached - trigger sections load
          if (typeof window.getShopifySectionsAfterCollection === 'function') {
            window.getShopifySectionsAfterCollection('block');
          } 
          this.remove();
        }
      } catch (err) {
        console.error("InfiniteScroll error:", err);
        this.isLoading = false;
        window.addEventListener("scroll", this.checkPosition, { passive: true });
        window.addEventListener("touchmove", this.checkPosition, { passive: true });
      }

      this.anchor.style.display = "none";
      this.anchor.innerText = "";
    }
  }
  
  if (!customElements.get("infinite-scroll")) {
    customElements.define("infinite-scroll", InfiniteScroll);
  }

  // Load-before: the mirror image of infinite-scroll. It sits at the TOP of the
  // grid and only renders when we're on a deeper page (?page=N, N>1) — i.e. when
  // the shopper arrived via the back button / a deep link. As they scroll up it
  // loads the previous page, prepends it (keeping the viewport stable) and swaps
  // in the previous page's own "before" element until page 1 is reached.
  export class InfiniteScrollBefore extends HTMLElement {
    constructor() {
      super();
      this.anchor = this.querySelector("a");
      if (!this.anchor) return;

      this.isLoading = false;
      // Start loading the previous page when its top edge is within this many
      // pixels of the viewport top (as the shopper scrolls up).
      this.threshold = 2000;
      this.lastY = window.scrollY || window.pageYOffset || 0;

      // Elements swapped in after a load are pre-armed so subsequent scroll-ups
      // are instant. The very first one waits briefly so the initial
      // scroll-to-product on arrival never counts as a "load before".
      this.active = this.hasAttribute("data-armed");

      this.checkPosition = this.checkPosition.bind(this);
      window.addEventListener("scroll", this.checkPosition, { passive: true });
      window.addEventListener("touchmove", this.checkPosition, { passive: true });
      window.addEventListener("resize", this.checkPosition, { passive: true });

      if (!this.active) {
        setTimeout(() => { this.active = true; }, 800);
      }
    }

    disconnectedCallback() {
      window.removeEventListener("scroll", this.checkPosition);
      window.removeEventListener("touchmove", this.checkPosition);
      window.removeEventListener("resize", this.checkPosition);
    }

    checkPosition() {
      const y = window.scrollY || window.pageYOffset || 0;
      const scrollingUp = y < this.lastY - 2;
      this.lastY = y;

      if (!this.active || this.isLoading || !this.anchor) return;
      // Only react to genuine upward scrolling, never the initial
      // programmatic scroll-to-product (which moves down).
      if (!scrollingUp) return;

      const rect = this.getBoundingClientRect();
      if (rect.bottom > -this.threshold) {
        this.loadPrevPage();
      }
    }

    async loadPrevPage() {
      this.isLoading = true;

      const href = this.anchor.getAttribute("href");
      if (!href) { this.isLoading = false; return; }

      const url = new URL(href, window.location.origin);
      const prevPage = new URLSearchParams(url.search).get("page") || "";

      try {
        const response = await fetch(url.toString());
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, "text/html");

        const newGrid = html.querySelector("collection-info [data-product-grid]");
        const collectionInfo = this.closest("collection-info");
        const grid = collectionInfo ? collectionInfo.querySelector("[data-product-grid]") : document.querySelector("[data-product-grid]");

        if (newGrid && grid) {
          // Pre-tag so the back-button observer ignores these prepended cards
          // and a click on one stores the correct page.
          if (prevPage) {
            newGrid.querySelectorAll("product-card, .product-card").forEach((card) => {
              card.setAttribute("data-collection-page", prevPage);
            });
          }
          // Prepend while keeping the current viewport position stable.
          const prevHeight = document.documentElement.scrollHeight;
          grid.insertAdjacentHTML("afterbegin", newGrid.innerHTML);
          const newHeight = document.documentElement.scrollHeight;
          window.scrollBy(0, newHeight - prevHeight);
        }

        // Swap in the previous page's own "before" element (page N-2, N-3...),
        // or remove it once page 1 is reached (page 1 renders no before element).
        const newBefore = html.querySelector("collection-info infinite-scroll-before");
        const hasValidAnchor = newBefore && newBefore.querySelector("a[href]");
        if (hasValidAnchor) {
          newBefore.setAttribute("data-armed", "");
          this.replaceWith(newBefore);
        } else {
          this.remove();
        }
      } catch (err) {
        console.error("InfiniteScrollBefore error:", err);
        this.isLoading = false;
      }
    }
  }

  if (!customElements.get("infinite-scroll-before")) {
    customElements.define("infinite-scroll-before", InfiniteScrollBefore);
  }